import type { AccountRow, ResultRow, VatMonthlyRow, AuditPeriod } from './types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];
const finite = (n: unknown): boolean => (typeof n === 'number' || typeof n === 'string')
    && String(n).trim() !== '' && Number.isFinite(Number(n));
const zero = (n: unknown): boolean => finite(n) && Number(n) === 0;
const validDate = (s: unknown): s is string => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)
    && Number.isFinite(Date.parse(s)) && new Date(s).toISOString().slice(0, 10) === s;
const samePeriod = (a: any, b: AuditPeriod): boolean => !!a
    && a.year === b.year && a.monthFrom === b.monthFrom && a.monthTo === b.monthTo;

// Recheck the persisted source contract against current inputs. This labels scope;
// it does not replace prepareAudit's strict validation or authorize any source.
function matchesSource(e: any, identity: string, period: AuditPeriod, method: string): boolean {
    if (!e || e.schema !== 'audit-data/v1' || e.source !== '24so' || e.identityId !== identity
        || !samePeriod(e.period, period) || e.completeness?.complete !== true
        || e.completeness.terminal !== true || e.completeness.method !== method) return false;
    if (method === 'GetAggregated' || e.fiscalYear) {
        const f = e.fiscalYear;
        const start = `${period.year}-${String(period.monthFrom).padStart(2, '0')}-01`;
        const end = new Date(Date.UTC(period.year, period.monthTo, 1)).toISOString().slice(0, 10);
        if (f?.status !== 'verified' || !validDate(f.start) || !validDate(f.endExclusive)
            || f.start > start || f.endExclusive < end) return false;
    }
    return true;
}

// Inspect each movement, not net totals that could hide cancelling entries.
function zeroMonths(months: unknown, period: AuditPeriod): boolean {
    if (!months || typeof months !== 'object' || Array.isArray(months)) return false;
    return Object.entries(months).every(([month, value]) => {
        const index = MONTHS.indexOf(month) + 1;
        return index > 0 && finite(value)
            && (index < period.monthFrom || index > period.monthTo || zero(value));
    });
}

export function hasVerifiedEmptyTransactions(
    meta: Record<string, any> | null, transactions: unknown, txError: unknown,
    saldo: AccountRow[], resultat: ResultRow[], vatMonthly: VatMonthlyRow[],
): boolean {
    if (txError != null || !Array.isArray(transactions) || transactions.length !== 0) return false;
    const e = meta?.transactionEvidence, p = meta?.period, s = meta?.sourceEvidence;
    if (e?.status !== 'verified' || e.count !== 0 || meta?.source !== '24so'
        || typeof meta.sourceIdentity !== 'string' || !meta.sourceIdentity
        || !p || ![p.year, p.monthFrom, p.monthTo].every(Number.isInteger)
        || p.year < 1900 || p.year > 2200 || p.monthFrom < 1 || p.monthTo > 12 || p.monthFrom > p.monthTo
        || s?.status !== 'verified' || s.source !== meta.source || s.sourceIdentity !== meta.sourceIdentity
        || !samePeriod(s.period, p)
        || !matchesSource(e.aggregated, meta.sourceIdentity, p, 'GetAggregated')
        || !matchesSource(e.transactions, meta.sourceIdentity, p, 'GetTransactions')
        || e.transactions.completeness.sourceRowCount !== 0) return false;
    return Array.isArray(saldo) && Array.isArray(resultat) && Array.isArray(vatMonthly)
        && saldo.every(r => r && zero(r.change) && finite(r.ib) && finite(r.ub)
            // IB is annual in the connector; a partial-period UB can differ.
            && (p.monthFrom !== 1 || Number(r.ib) === Number(r.ub))
            && (r.months == null || zeroMonths(r.months, p)))
        && resultat.every(r => r && zero(r.sum) && (r.change == null || zero(r.change)) && zeroMonths(r.months, p))
        && vatMonthly.every(r => r && zeroMonths(r.monthlyChange, p));
}
